import { Pressable, StyleSheet, Text, View, type LayoutChangeEvent } from "react-native";
import { useTranslation } from "react-i18next";
import { roundDone } from "./useRoundLock";
import {
  bandLabel,
  formatDraftGridCell,
  formatDraftWeekLine,
  kindAt,
  LOAD_UNWRITTEN,
  NO_LOAD,
  programRoundLabel,
  rectHas,
  slotRoundBand,
  type CellPos,
  type CellRect,
  type CurrentMax,
  type DraftGridLens,
  type DraftLineOptions,
  type RoundType,
  type SlotDraft,
  type UnitsPreference,
} from "@heraion/shared";

import type { GridGestureBundle } from "../IntensityGrid/useGridGestures";
import type { useRoundTypeWords } from "../primitives/RoundTypeBadge";
import type { UiTheme } from "../theme";
import { LINE, RING, ROW_MIN, RoundGridCell, type CellFieldSpec } from "./RoundGridCell";
import type { gridIds } from "./gridKeyContract";
import { DoneCheck, doneWord } from "./useWeekMark";

/** The least height of the head row (over the 44pt tap floor). */
export const HEAD_MIN = 48;
/** The least width of a round column. */
export const COL_MIN = 72;

/** What every round column of the day grid reads. */
export type ColumnCtx = {
  theme: UiTheme;
  slots: readonly SlotDraft[];
  lens: DraftGridLens;
  lineOptions: DraftLineOptions;
  /** The cells' own: no «· ≈%» in a narrow column read only. */
  cellOptions: DraftLineOptions;
  /** Each round's share of the width, once measured. */
  colW?: number;
  readOnly: boolean;
  cellUnit: UnitsPreference;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  resolveName: (id: string, snapshot?: string) => string;
  /** The rounds by hand of each exercise. */
  hand: readonly ReadonlySet<number>[];
  words: ReturnType<typeof useRoundTypeWords>;
  typeOf: (w: number) => RoundType;
  headH: number;
  heightOf: (id: string, min: number) => number;
  report: (key: string, extra: number) => (e: LayoutChangeEvent) => void;
  /** This grid's DOM ids (useGridEditing). */
  ids: ReturnType<typeof gridIds>;
  open: CellPos | null;
  /** The selected block, or the one a fill would cover. */
  range: CellRect | null;
  /** Where the fill handle sits, when there is one. */
  handleAt: CellPos | null;
  /** The open cell's field, while it is open; `name` and `round` name it. */
  fieldAt: (p: CellPos, name: string, round: string) => CellFieldSpec | undefined;
  onCell: (p: CellPos) => void;
  /** A cell's hold and screen-reader actions. */
  cellGesture: (p: CellPos) => GridGestureBundle;
  /** Rounds 0..lockedThrough were executed: their heads carry «✓». */
  lockedThrough?: number;
  /** Absent (read only, #358): the head is a label, no round menu. */
  onHead?: (w: number) => void;
};

const at = (p: CellPos | null, row: number, col: number) => !!p && p.row === row && p.col === col;

// One round of the wide day grid (#336 slice 6): its head, which opens the
// round menu, and a cell per exercise. Out of ExerciseRoundGrid for its size.
export function RoundColumn({ ctx, w, styles: g }: { ctx: ColumnCtx; w: number; styles: ColumnStyles }) {
  const { t } = useTranslation();
  const off = t("editorV3.notInRound", { defaultValue: "Not in this round" });
  const noMax = t("editorV3.grid.noMaxA11y", { defaultValue: "No MAX to work out the load" });
  const unwritten = t("editorV3.grid.loadUnwrittenA11y", { defaultValue: "Kilos not written yet" });
  const handWord = t("editorV3.rule.byHandMark", { defaultValue: "by hand" });
  const type = ctx.typeOf(w);
  const marked = type !== "normal";
  const done = roundDone(w, ctx.lockedThrough);
  const label = [programRoundLabel(w + 1, type, ctx.words), done ? doneWord(t) : null].filter(Boolean).join(", ");
  const onHead = ctx.onHead;
  const head = (
    <View onLayout={ctx.report(`head:${w}`, LINE)} style={g.headContent}>
      <Text style={g.round}>{programRoundLabel(w + 1)}</Text>
      <Text style={g.type}>{marked ? ctx.words[type] : " "}</Text>
      {done ? <DoneCheck theme={ctx.theme} /> : null}
    </View>
  );
  return (
    <View style={[g.column, ctx.colW ? { width: ctx.colW } : null, marked && type === "deload" ? g.deload : null]}>
      {onHead ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("editorV3.grid.roundHeadA11y", { round: label, defaultValue: "{{round}}, round menu" })}
          onPress={() => onHead(w)}
          style={({ pressed }) => [g.head, g.roundHead, { height: ctx.headH }, pressed && g.pressed]}
        >
          {head}
        </Pressable>
      ) : (
        <View accessible accessibilityLabel={label} style={[g.head, g.roundHead, { height: ctx.headH }]}>
          {head}
        </View>
      )}
      {ctx.slots.map((slot, slotIdx) => {
        const name = ctx.resolveName(slot.exerciseId, slot.exerciseName);
        const max = ctx.maxMeta?.get(slot.exerciseId);
        const text = formatDraftGridCell(slot, w, ctx.lens, ctx.cellOptions, max);
        const full = ctx.cellOptions === ctx.lineOptions ? text : formatDraftGridCell(slot, w, ctx.lens, ctx.lineOptions, max);
        const band = slotRoundBand(slot, w, { max, cellUnit: ctx.cellUnit })?.band ?? null;
        const byHand = text !== null && ctx.hand[slotIdx]!.has(w);
        const line = formatDraftWeekLine(slot, w, ctx.lineOptions);
        const first = slot.setRows[0];
        const lacksMax = ctx.lens === "load" && text === NO_LOAD && !!first && kindAt(first, w) !== "absolute";
        const spoken = [
          t("editorV3.grid.cellA11y", { name, round: label, line: line ?? off, defaultValue: "{{name}}, {{round}}: {{line}}" }),
          band ? bandLabel(t, band) : null,
          lacksMax ? noMax : null,
          ctx.lens === "load" && text === LOAD_UNWRITTEN ? unwritten : null,
          full !== text ? full : null,
          byHand ? handWord : null,
        ]
          .filter(Boolean)
          .join(", ");
        const pos = { row: slotIdx, col: w };
        const open = at(ctx.open, slotIdx, w);
        return (
          <RoundGridCell
            key={slot.id}
            theme={ctx.theme}
            id={ctx.ids.cell(pos)}
            cell={{ text, band, byHand, spoken, open, inRange: !!ctx.range && rectHas(ctx.range, pos) }}
            height={ctx.heightOf(slot.id, ROW_MIN)}
            onLayout={ctx.report(`${slot.id}:${w}`, LINE + 2 * RING)}
            onPress={() => ctx.onCell(pos)}
            gesture={ctx.cellGesture(pos)}
            field={open && text !== null ? ctx.fieldAt(pos, name, label) : undefined}
            fillHandle={at(ctx.handleAt, slotIdx, w) ? ctx.ids.fill : undefined}
            readOnly={ctx.readOnly}
          />
        );
      })}
    </View>
  );
}

export type ColumnStyles = ReturnType<typeof makeColumnStyles>;

/** The round columns' styles; the grid's own head cells take `head` too. */
export function makeColumnStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    column: { minWidth: COL_MIN, flexGrow: 1 },
    deload: { backgroundColor: p.surface2 },
    head: { minHeight: HEAD_MIN, justifyContent: "center", borderBottomWidth: 1, borderBottomColor: p.border },
    roundHead: { alignItems: "center", paddingHorizontal: s.s1 },
    headContent: { alignItems: "center", paddingVertical: s.s1 },
    round: { color: p.ink2, fontSize: ty.sizes.sm, fontFamily: ty.fonts.mono, fontWeight: "700" },
    type: { color: p.ink3, fontSize: ty.sizes.sm, textAlign: "center" },
    pressed: { backgroundColor: p.surface2 },
  });
}
